// spec 260509-system-monitor C-4：③ 段模型（用户反馈：两个模型用同样的紧凑
// 行样式，不再做 primary 大行 + secondary 折叠的视觉差）。
//
// 排序：critical → queued → active → tok/s → LLM 默认（沿用
// ``useDerivePrimaryModel`` 的"主因优先级"，第一行展示主因模型，第二行展示次
// 模型，但两行视觉一致）。每行都点击跳到概览页对应 model 子分块。

import type { MouseEvent, ReactElement } from 'react';

import { useDerivePrimaryModel } from '../../state/derive';
import type { ServiceCard } from '../../state/system-types';
import { navigateToDashboard } from '../navigateToDashboard';

function isCritical(s: ServiceCard | null): boolean {
  if (!s) return false;
  const dur = s.estimated_unhealthy_for_seconds;
  return typeof dur === 'number' && dur >= 10;
}

function modelAnchor(s: ServiceCard): string {
  return `#model-${s.kind ?? 'unknown'}`;
}

function modelAnchorBare(s: ServiceCard): string {
  return `model-${s.kind ?? 'unknown'}`;
}

function onModelRowClick(s: ServiceCard) {
  return (e: MouseEvent<HTMLAnchorElement>): void => {
    e.preventDefault();
    navigateToDashboard(modelAnchorBare(s));
  };
}

interface ModelRowProps {
  model: ServiceCard;
  /** 该行在 widget 里的位置（仅用于 data-* 标记 + aria-label，不影响样式）。 */
  position: 'primary' | 'secondary';
}

/** 紧凑模型行：name + tok/s + ▶active + ⏸queued。LLM / ASR 用同款。 */
function ModelRow({ model, position }: ModelRowProps): ReactElement {
  const m = model.metrics;
  const tokens = m?.tokens_per_sec ?? null;
  const cls = `sysmon-model-row${isCritical(model) ? ' is-crit' : ''}`;
  const ariaLabel =
    position === 'primary'
      ? `主模型 ${model.display_name ?? model.name}`
      : `次模型 ${model.display_name ?? model.name}`;

  return (
    <a
      className={cls}
      href={modelAnchor(model)}
      onClick={onModelRowClick(model)}
      data-model-row={position}
      data-kind={model.kind ?? 'unknown'}
      aria-label={ariaLabel}
    >
      <span className="sysmon-model-row-name">
        {model.display_name ?? model.name}
      </span>
      <span className="sysmon-model-row-tokens">
        {tokens === null ? '--' : `${tokens.toFixed(1)} tok/s`}
      </span>
      {m?.running !== null && m?.running !== undefined && (
        <span className="sysmon-model-row-active">▶ {m.running}</span>
      )}
      {m?.waiting !== null && m?.waiting !== undefined && (
        <span className="sysmon-model-row-queued">⏸ {m.waiting}</span>
      )}
    </a>
  );
}

export function ModelSection(): ReactElement | null {
  const { primary, secondary } = useDerivePrimaryModel();

  if (primary === null) {
    return (
      <div className="sysmon-section sysmon-model" data-section="model">
        <div className="sysmon-model-empty">无模型部署</div>
      </div>
    );
  }

  return (
    <div className="sysmon-section sysmon-model" data-section="model">
      <ModelRow model={primary} position="primary" />
      {secondary !== null && <ModelRow model={secondary} position="secondary" />}
    </div>
  );
}

export default ModelSection;
